import Box from "@mui/material/Box";
import GalleryItem from "./GalleryItem";
import Masonry from "@mui/lab/Masonry";
import Typography from "@mui/material/Typography";
import { useTranslationApp } from "../../lib/i18next";
import { MODAL_GALLERY_NO_MORE_CONTENT } from "../../i18n/keysTranslations";
import Loader from "../Loaders/Loader";
import React, { useMemo, memo, useState, useCallback } from "react";
import { getGalleryItemSelectionId } from "../../utils/gallerySelection";
import { MEDIA_DIMENSIONS } from "../../utils/constants";
import VirtualMasonry from "./VirtualMasonry";

const getGalleryItemKey = (item, originalIndex) =>
  item.stableKey ||
  item.id ||
  item.url ||
  `index-${item.creationTime || originalIndex}`;

const FORMAT_ASPECT_RATIOS = {
  [MEDIA_DIMENSIONS.square]: 1,
  [MEDIA_DIMENSIONS.horizontal]: 16 / 9,
  [MEDIA_DIMENSIONS.feed]: 4 / 5,
};

// Mirrors the media container ratio GalleryItem renders, so the virtual
// layout can estimate card heights before they are measured.
const getEstimatedAspectRatio = (item, itemDimensions) => {
  const resizing = item.resizingFormat;
  if (resizing && resizing !== "animate") {
    if (resizing === MEDIA_DIMENSIONS.custom) {
      if (item.resizingWidth && item.resizingHeight) {
        return item.resizingWidth / item.resizingHeight;
      }
    }
    return FORMAT_ASPECT_RATIOS[resizing] || 9 / 16;
  }
  if (item.width && item.height) return item.width / item.height;
  const url = item.carouselImages?.[0]?.url || item.url;
  const cached = url ? itemDimensions?.get?.(url) : null;
  if (cached?.width && cached?.height) return cached.width / cached.height;
  return 4 / 5;
};

const Gallery = memo(
  ({
    data = [],
    onCloseModal = () => {},
    lastItemRef,
    loadLastItem,
    onFailedLoadUrl = () => {},
    onSelectItem = () => {},
    onSelectPreview = () => {},
    onCallbackResizeFormat = () => {},
    onAnimateImage = () => {},
    onDeleteItem = () => {},
    onRemoveItemFromFolder = null,
    onDownloadItems = () => {},
    listRoot = "contentList",
    selectedItems = [],
    multiple,
    from,
    acceptedFormats = [],
    minDimensions = {},
    showDimensionsOverlay = true,
    showActionButtons = true,
    onEditWithCanva,
    onEditWithIA,
    onEditWithVideo,
    itemGeneratingComponent,
    sxGallery = {},
    showEmptyMessage = true,
    isLoading = false,
    showRating = false,
    showResizeFormatButton = false,
    showEditButton,
    showDeleteButton = true,
    showPreviewExpandOnly = false,
    showExpandButton = false,
    showFooterEditButton = false,
    onFooterEdit = () => {},
    showFooterDeleteButton = false,
    onFooterDelete = () => {},
    overlayPickerMode = false,
    showDownloadButton,
    loadingItems,
    loadingComponent,
    itemFailedComponent,
    columns = { xs: 1, sm: 2, md: 3, lg: 4, xl: 5 },
    LayoutComponent = Masonry,
    propsMedia = {},
    itemDimensions,
    scheduledDate = null,
    fromCalendar = false,
    selectedItemsArray = [],
    selectionBy = "index",
    showFeedbackButtons = false,
    onFeedback,
    showReportButton = false,
    showPostButton = true,
    onRefreshCalendar,
    onScheduledShareSuccess,
    onShareSuccess,
    showContentApproval = false,
    getContentApprovalData = () => null,
    enablingContentApprovalId = null,
    showContentApprovalEnableButton = true,
    onEnableContentApproval = () => {},
    onCopyContentApprovalLink = () => {},
    onOpenContentApprovalReview = () => {},
    showShimmer = false,
    highlightedIds = [],
    draggableForFolders = false,
    selectedItemsCount = 0,
    showAssetName = false,
    onRenameAsset = () => {},
    actionStripMinHeight = 48,
    releaseMediaWhenHidden = false,
    // Opt-in: virtualized masonry, only cards near the viewport of
    // scrollElementRef are mounted.
    virtualized = false,
    scrollElementRef = null,
  }) => {
    const { t } = useTranslationApp();
    const [unmutedVideoKey, setUnmutedVideoKey] = useState(null);

    const onRequestVideoUnmute = useCallback(({ videoMuteKey }) => {
      setUnmutedVideoKey(videoMuteKey);
    }, []);

    const onRequestVideoMute = useCallback(() => {
      setUnmutedVideoKey(null);
    }, []);

    const onSelectPreviewWithMute = useCallback(
      (indexOrData) => {
        setUnmutedVideoKey(null);
        onSelectPreview(indexOrData);
      },
      [onSelectPreview]
    );

    const onEditWithVideoWithMute = useCallback(
      (videoData) => {
        setUnmutedVideoKey(null);
        onEditWithVideo?.(videoData);
      },
      [onEditWithVideo]
    );

    // Memoizar los items filtrados para evitar recalcular en cada render
    const validItems = useMemo(() => {
      return data
        .map((item, index) => ({ item, originalIndex: index }))
        .filter(
          ({ item }) => (item && item.url) || item.isGenerating || item.isFailed
        );
    }, [data]);

    const lastValidOriginalIndex = useMemo(() => {
      if (validItems.length === 0) return -1;
      return validItems[validItems.length - 1].originalIndex;
    }, [validItems]);

    // Position (1-based) of each selected key, to avoid includes/indexOf per item.
    const selectionPositionByKey = useMemo(() => {
      const positions = new Map();
      (selectedItemsArray || []).forEach((key, position) => {
        if (!positions.has(key)) positions.set(key, position + 1);
      });
      return positions;
    }, [selectedItemsArray]);

    const highlightedIdSet = useMemo(() => {
      if (!Array.isArray(highlightedIds) || highlightedIds.length === 0) {
        return new Set();
      }
      return new Set(highlightedIds.map((value) => String(value)));
    }, [highlightedIds]);

    const isItemHighlighted = useCallback(
      (galleryItem) => {
        if (highlightedIdSet.size === 0 || !galleryItem) {
          return false;
        }

        const candidateKeys = [
          galleryItem.id,
          galleryItem.stableKey,
          galleryItem.url,
        ]
          .filter(Boolean)
          .map((value) => String(value));

        return candidateKeys.some((key) => highlightedIdSet.has(key));
      },
      [highlightedIdSet]
    );

    const renderValidItem = ({ item, originalIndex }, { withLastItemRef }) => {
      const uniqueKey = getGalleryItemKey(item, originalIndex);

      if (item.isGenerating) {
        return (
          <Box key={uniqueKey} sx={{ flexShrink: 0 }}>
            {itemGeneratingComponent &&
              React.createElement(itemGeneratingComponent.component, {
                data: item,
                ...itemGeneratingComponent.props,
              })}
          </Box>
        );
      }
      if (item.isFailed) {
        return (
          <Box key={uniqueKey} sx={{ flexShrink: 0 }}>
            {itemFailedComponent?.component
              ? React.createElement(itemFailedComponent.component, {
                  data: item,
                  reason: item.reason,
                  ...itemFailedComponent.props,
                })
              : null}
          </Box>
        );
      }
      const itemSelectionId =
        selectionBy === "id" ? getGalleryItemSelectionId(item) : null;

      return (
        <Box key={uniqueKey} sx={{ flexShrink: 0 }}>
          <GalleryItem
            item={item}
            index={originalIndex}
            onCloseModalGallery={onCloseModal}
            onFailedLoadUrl={onFailedLoadUrl}
            onSelectItem={onSelectItem}
            onSelectPreview={onSelectPreviewWithMute}
            onCallbackResizeFormat={onCallbackResizeFormat}
            onAnimateImage={onAnimateImage}
            onDeleteItem={onDeleteItem}
            onRemoveFromFolder={onRemoveItemFromFolder}
            onDownloadItems={onDownloadItems}
            listRoot={listRoot}
            multiple={multiple}
            isSelected={
              selectionBy === "id"
                ? Boolean(
                    itemSelectionId &&
                      (selectedItems instanceof Set
                        ? selectedItems.has(itemSelectionId)
                        : selectedItems.includes(itemSelectionId))
                  )
                : selectedItems instanceof Set
                  ? selectedItems.has(originalIndex)
                  : selectedItems.includes(originalIndex)
            }
            from={from}
            acceptedFormats={acceptedFormats}
            minDimensions={minDimensions}
            showResizeFormatButton={showResizeFormatButton}
            showEditButton={showEditButton}
            showPreviewExpandOnly={showPreviewExpandOnly}
            showExpandButton={showExpandButton}
            showFooterEditButton={showFooterEditButton}
            onFooterEdit={onFooterEdit}
            showFooterDeleteButton={showFooterDeleteButton}
            onFooterDelete={onFooterDelete}
            showDimensionsOverlay={showDimensionsOverlay}
            showActionButtons={showActionButtons}
            showDeleteButton={showDeleteButton}
            overlayPickerMode={overlayPickerMode}
            showDownloadButton={showDownloadButton}
            onEditWithCanva={onEditWithCanva}
            onEditWithIA={onEditWithIA}
            onEditWithVideo={onEditWithVideoWithMute}
            propsMedia={propsMedia}
            itemDimensions={itemDimensions}
            showRating={showRating}
            scheduledDate={scheduledDate}
            fromCalendar={fromCalendar}
            selectionNumber={
              selectionBy === "id"
                ? (itemSelectionId &&
                    selectionPositionByKey.get(itemSelectionId)) ||
                  null
                : selectionPositionByKey.get(originalIndex) || null
            }
            showFeedbackButtons={showFeedbackButtons}
            onFeedback={onFeedback}
            showReportButton={showReportButton}
            showPostButton={showPostButton}
            onRefreshCalendar={onRefreshCalendar}
            onScheduledShareSuccess={onScheduledShareSuccess}
            onShareSuccess={onShareSuccess}
            showContentApproval={showContentApproval}
            getContentApprovalData={getContentApprovalData}
            enablingContentApprovalId={enablingContentApprovalId}
            showContentApprovalEnableButton={showContentApprovalEnableButton}
            onEnableContentApproval={onEnableContentApproval}
            onCopyContentApprovalLink={onCopyContentApprovalLink}
            onOpenContentApprovalReview={onOpenContentApprovalReview}
            showShimmer={showShimmer}
            unmutedVideoKey={unmutedVideoKey}
            onRequestVideoUnmute={onRequestVideoUnmute}
            onRequestVideoMute={onRequestVideoMute}
            isHighlighted={isItemHighlighted(item)}
            draggableForFolders={draggableForFolders}
            itemSelectionId={itemSelectionId}
            selectedItemsCount={selectedItemsCount}
            showAssetName={showAssetName}
            onRenameAsset={onRenameAsset}
            actionStripMinHeight={actionStripMinHeight}
            releaseWhenHidden={releaseMediaWhenHidden}
          />
          {withLastItemRef && originalIndex === lastValidOriginalIndex && (
            <div
              ref={lastItemRef}
              style={{ minHeight: 1, width: "100%" }}
              aria-hidden="true"
            />
          )}
        </Box>
      );
    };

    const useVirtualLayout = Boolean(virtualized && scrollElementRef);
    const [draggingKey, setDraggingKey] = useState(null);

    const virtualEntries = useMemo(() => {
      if (!useVirtualLayout) return [];
      const entries = [];
      if (isLoading && loadingComponent && loadingItems) {
        for (let index = 0; index < loadingItems; index++) {
          entries.push({ key: `loading-${index}`, type: "loading" });
        }
      }
      validItems.forEach((validItem) => {
        const { item, originalIndex } = validItem;
        entries.push({
          key: getGalleryItemKey(item, originalIndex),
          type: "item",
          validItem,
          aspectRatio: getEstimatedAspectRatio(item, itemDimensions),
          // Generating/failed cards don't share the regular card chrome.
          sampleChrome: !item.isGenerating && !item.isFailed,
        });
      });
      return entries;
    }, [
      useVirtualLayout,
      isLoading,
      loadingComponent,
      loadingItems,
      validItems,
      itemDimensions,
    ]);

    const renderVirtualEntry = (entry) => {
      if (entry.type === "loading") {
        return <Box>{loadingComponent?.component}</Box>;
      }
      return (
        <Box
          onDragStart={() => setDraggingKey(entry.key)}
          onDragEnd={() => setDraggingKey(null)}
        >
          {renderValidItem(entry.validItem, { withLastItemRef: false })}
        </Box>
      );
    };

    return (
      <Box sx={{ pr: 4, pl: 5, ...sxGallery, overflowAnchor: "none" }}>
        {data?.length === 0 && !loadLastItem && (
          <div
            ref={lastItemRef}
            style={{ minHeight: 1, width: "100%" }}
            aria-hidden="true"
          />
        )}
        {useVirtualLayout ? (
          <VirtualMasonry
            entries={virtualEntries}
            renderEntry={renderVirtualEntry}
            scrollElementRef={scrollElementRef}
            columns={columns}
            spacing={2}
            pinnedKey={draggingKey}
          />
        ) : (
          <LayoutComponent columns={columns} spacing={2}>
            {isLoading &&
              loadingComponent &&
              loadingItems &&
              Array.from({ length: loadingItems }).map((_, index) => (
                <Box key={index}>{loadingComponent.component}</Box>
              ))}
            {validItems.length > 0 &&
              validItems.map((validItem) =>
                renderValidItem(validItem, { withLastItemRef: true })
              )}
          </LayoutComponent>
        )}
        {useVirtualLayout && validItems.length > 0 && (
          <div
            ref={lastItemRef}
            style={{ minHeight: 1, width: "100%" }}
            aria-hidden="true"
          />
        )}

        {isLoading &&
          loadingComponent &&
          !loadingItems &&
          loadingComponent.component}

        {!loadingComponent && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
              mt: 2,
            }}
          >
            {!loadLastItem && <Loader size={30} sx={{ mb: 2 }} />}
            {loadLastItem && data?.length > 0 && showEmptyMessage && (
              <Typography variant="h6" sx={{ color: "white" }}>
                {t(MODAL_GALLERY_NO_MORE_CONTENT)}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  },
  (prevProps, nextProps) => {
    if (prevProps.data !== nextProps.data) return false;
    if (prevProps.data?.length !== nextProps.data?.length) return false;
    if (prevProps.isLoading !== nextProps.isLoading) return false;
    if (prevProps.loadLastItem !== nextProps.loadLastItem) return false;
    if (prevProps.selectedItems !== nextProps.selectedItems) return false;
    if (prevProps.selectionBy !== nextProps.selectionBy) return false;
    if (prevProps.overlayPickerMode !== nextProps.overlayPickerMode)
      return false;
    if (prevProps.showDownloadButton !== nextProps.showDownloadButton)
      return false;
    if (prevProps.listRoot !== nextProps.listRoot) return false;
    if (prevProps.showDeleteButton !== nextProps.showDeleteButton) return false;
    if (prevProps.onRemoveItemFromFolder !== nextProps.onRemoveItemFromFolder)
      return false;
    if (prevProps.showEditButton !== nextProps.showEditButton) return false;
    if (prevProps.showPreviewExpandOnly !== nextProps.showPreviewExpandOnly)
      return false;
    if (prevProps.showExpandButton !== nextProps.showExpandButton) return false;
    if (prevProps.showFooterDeleteButton !== nextProps.showFooterDeleteButton)
      return false;
    if (prevProps.onFooterDelete !== nextProps.onFooterDelete) return false;
    if (prevProps.showFooterEditButton !== nextProps.showFooterEditButton)
      return false;
    if (prevProps.showReportButton !== nextProps.showReportButton) return false;
    if (prevProps.showPostButton !== nextProps.showPostButton) return false;
    if (prevProps.onRefreshCalendar !== nextProps.onRefreshCalendar)
      return false;
    if (prevProps.onScheduledShareSuccess !== nextProps.onScheduledShareSuccess)
      return false;
    if (prevProps.onShareSuccess !== nextProps.onShareSuccess) return false;
    if (prevProps.showRating !== nextProps.showRating) return false;
    if (prevProps.itemDimensions !== nextProps.itemDimensions) return false;
    if (
      prevProps.enablingContentApprovalId !==
      nextProps.enablingContentApprovalId
    )
      return false;
    if (prevProps.showContentApproval !== nextProps.showContentApproval)
      return false;
    if (
      prevProps.showContentApprovalEnableButton !==
      nextProps.showContentApprovalEnableButton
    )
      return false;
    if (prevProps.draggableForFolders !== nextProps.draggableForFolders)
      return false;
    if (prevProps.selectedItemsCount !== nextProps.selectedItemsCount)
      return false;
    if (prevProps.showAssetName !== nextProps.showAssetName) return false;
    if (prevProps.actionStripMinHeight !== nextProps.actionStripMinHeight)
      return false;
    if (prevProps.releaseMediaWhenHidden !== nextProps.releaseMediaWhenHidden)
      return false;
    if (prevProps.virtualized !== nextProps.virtualized) return false;
    if (prevProps.scrollElementRef !== nextProps.scrollElementRef) return false;

    if (prevProps.showShimmer !== nextProps.showShimmer) return false;
    if (
      JSON.stringify(prevProps.highlightedIds) !==
      JSON.stringify(nextProps.highlightedIds)
    ) {
      return false;
    }

    // Comparar arrays de selectedItemsArray
    if (
      prevProps.selectedItemsArray?.length !==
      nextProps.selectedItemsArray?.length
    )
      return false;
    if (
      prevProps.selectedItemsArray !== nextProps.selectedItemsArray &&
      prevProps.selectedItemsArray?.some(
        (key, position) => key !== nextProps.selectedItemsArray[position]
      )
    )
      return false;

    if (prevProps.LayoutComponent !== nextProps.LayoutComponent) return false;
    if (prevProps.loadingItems !== nextProps.loadingItems) return false;
    if (prevProps.itemFailedComponent !== nextProps.itemFailedComponent)
      return false;

    const prevCols = prevProps.columns;
    const nextCols = nextProps.columns;
    if (
      prevCols?.xs !== nextCols?.xs ||
      prevCols?.sm !== nextCols?.sm ||
      prevCols?.md !== nextCols?.md ||
      prevCols?.lg !== nextCols?.lg ||
      prevCols?.xl !== nextCols?.xl
    ) {
      return false;
    }

    return true;
  }
);

Gallery.displayName = "Gallery";

export default Gallery;
